import { Button, Group, Tooltip } from '@mantine/core';
import AppIcon from '@/components/AppIcon.tsx';
import { HiLink, HiMagnifyingGlass, HiOutlineClock, HiOutlineCodeBracketSquare } from 'react-icons/hi2';
import { useState } from 'react';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import classes from '@/pages/Run/components/StepCardActions.module.css';
import RerunStepButton from '@/components/RerunStepButton.tsx';
import { Run } from '@/types/runs.ts';
import { StepExecution, StepRun } from '@/types/stepExecutions.ts';
import { AutomationStep, AutomationStepMap } from '@/types/automationSteps.ts';
import StepDebugPreviewModal from '@/pages/Run/components/StepDebugPreviewModal.tsx';
import StepRunHistoryModal from '@/pages/Run/components/StepRunHistoryModal.tsx';
import useIsAdmin from '@/hooks/useIsAdmin.ts';
import StepExecutionMetadataPanel from '@/pages/Run/components/StepExecutionMetadataPanel.tsx';

type props = {
  run: Run;
  stepRun?: StepRun;
  stepExecution?: StepExecution;
  step: AutomationStep;
  stepsById?: AutomationStepMap;
  showReferences?: boolean;
  onToggleReferences?: () => void;
  rerunDisabledMessage?: string;
};

const StepCardAction = ({
  run,
  stepRun,
  stepExecution,
  step,
  stepsById,
  showReferences,
  onToggleReferences,
  rerunDisabledMessage,
}: props) => {
  const [showMetadata, setShowMetadata] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showDebugPreview, setShowDebugPreview] = useState(false);
  const isAdmin = useIsAdmin();
  const hasStepExecution = !!stepExecution?.id;

  return (
    <>
      <Group gap="7px">
        <RerunStepButton
          run={run}
          stepRun={stepRun}
          stepExecution={stepExecution}
          step={step}
          disabledMessage={rerunDisabledMessage}
        />

        <Tooltip
          label="Metadata is available after the step execution is loaded"
          disabled={hasStepExecution}
          openDelay={200}
        >
          <Button
            onClick={() => setShowMetadata(!showMetadata)}
            size={'xs'}
            variant={'default'}
            classNames={{ root: classes.button }}
            disabled={!hasStepExecution}
          >
            <Group gap={'4px'}>
              <AppIcon Icon={HiOutlineCodeBracketSquare} size={16} />
              Metadata
            </Group>
          </Button>
        </Tooltip>

        {onToggleReferences && (
          <Tooltip
            label="References are available after the step run is loaded"
            disabled={!!stepRun}
            openDelay={200}
          >
            <Button
              onClick={() => onToggleReferences()}
              size={'xs'}
              variant={showReferences ? 'light' : 'default'}
              classNames={{ root: classes.button }}
              disabled={!stepRun}
            >
              <Group gap={'4px'}>
                <AppIcon Icon={HiLink} size={16} />
                References
              </Group>
            </Button>
          </Tooltip>
        )}
        <Tooltip
          label="History is available after the step run is loaded"
          disabled={!!stepRun}
          openDelay={200}
        >
          <Button
            onClick={() => setShowHistory(true)}
            size={'xs'}
            variant={'default'}
            classNames={{ root: classes.button }}
            disabled={!stepRun}
          >
            <Group gap={'4px'}>
              <AppIcon Icon={HiOutlineClock} size={16} />
              History
            </Group>
          </Button>
        </Tooltip>
        {isAdmin && (
          <Tooltip label="Debug preview is available after the step run is loaded" disabled={!!stepRun} openDelay={200}>
            <Button
              onClick={() => setShowDebugPreview(true)}
              size={'xs'}
              variant={'default'}
              classNames={{ root: classes.button }}
              disabled={!stepRun}
            >
              <Group gap={'4px'}>
                <AppIcon Icon={HiMagnifyingGlass} size={16} />
                Debug
              </Group>
            </Button>
          </Tooltip>
        )}
      </Group>
      <StyledModal
        opened={showMetadata}
        onClose={() => setShowMetadata(false)}
        title={
          step.name +
          (stepRun ? ` - Step Run: ${stepRun?.id}` : '') +
          (stepExecution ? ` - Step Execution: ${stepExecution?.id}` : '')
        }
        centered
        size={'xl'}
        withCloseButton={true}
        scrollType="none"
      >
        <StepExecutionMetadataPanel
          run={run}
          step={step}
          stepRun={stepRun}
          stepExecution={stepExecution}
          enabled={showMetadata}
        />
      </StyledModal>
      {showHistory && (
        <StepRunHistoryModal
          opened={showHistory}
          onClose={() => setShowHistory(false)}
          run={run}
          step={step}
          stepRun={stepRun}
          stepsById={stepsById || {}}
        />
      )}
      {showDebugPreview && (
        <StepDebugPreviewModal
          opened={showDebugPreview}
          onClose={() => setShowDebugPreview(false)}
          run={run}
          step={step}
          stepRun={stepRun}
        />
      )}
    </>
  );
};

export default StepCardAction;
